// ════════════════════════════════════════════════════════════════════════
//  Welcher Test jetzt dran ist – und ob die Lektionen warten müssen
//
//  Reine Rechnung aus dem, was die Anmeldung liefert. Kein Zustand, keine
//  Abfrage, kein React: dieselbe Bauart wie `lock.ts` nebenan, und aus demselben
//  Grund prüfbar ohne Bildschirm.
//
//  Der Server sperrt nichts. Er sagt, welchen Test der Lehrgang hat und was
//  dieses Kind schon gemessen hat; ob daraus „erst der Test“ wird, entscheidet
//  die App – dort steht auch der Satz, den das Kind liest.
// ════════════════════════════════════════════════════════════════════════

import type { Course, Measurement } from "../../../shared/lib/backend.js";
import { isDone } from "../../courses/lib/lock.js";

export type Phase = "vorher" | "nachher";

export function measurementOf(course: Course, phase: Phase): Measurement | null {
  // `?? []`, weil die Lehrgänge auch aus dem sessionStorage kommen können: ein
  // Eintrag, der vor dieser Fassung dort landete, kennt das Feld nicht. Ein
  // Absturz beim Neuladen wäre die härteste Art, davon zu erfahren.
  return (course.tests ?? []).find((m) => m.phase === phase) ?? null;
}

/**
 * Der Test, der jetzt ansteht – oder null.
 *
 * `vorher`, solange er fehlt: er ist der erste Schritt, und danach wird er nie
 * wieder angeboten.
 *
 * `nachher` erst, wenn der Lehrgang durch ist (jede Stufe einmal geübt) UND ein
 * Eingangswert vorliegt. Ohne den ersten Wert wäre der zweite eine Zahl ohne
 * Vergleich – gemessen würde etwas, das niemand einordnen kann.
 */
export function pendingAssessment(course: Course | null): Phase | null {
  if (!course || (course.test ?? null) === null) return null;
  if (measurementOf(course, "vorher") === null) return "vorher";
  if (measurementOf(course, "nachher") !== null) return null;
  return isDone(course) ? "nachher" : null;
}

/**
 * Warten die Lektionen noch auf den Eingangstest?
 *
 * Nur der EINGANGSTEST hält auf. Der Abschlusstest steht am Ende und sperrt
 * nichts – wer ihn nicht machen will, hat den Lehrgang trotzdem geübt.
 */
export function blockedByAssessment(course: Course | null): boolean {
  return pendingAssessment(course) === "vorher";
}
